import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  MenuItem,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';
import { auth, db, functions } from '../../firebase';
import ChatQueueColumn from './ChatOperationsHub/ChatQueueColumn';
import ChatStageColumn from './ChatOperationsHub/ChatStageColumn';
import ChatInfoPanel from './ChatOperationsHub/ChatInfoPanel';
import ResizableSplitLayout from './ChatOperationsHub/ResizableSplitLayout';
import { getChatSettings } from '../../lib/companyProfile';
import {
  debounce,
  normalizeTypingState,
  TYPING_IDLE_MS,
  TYPING_SIGNAL_DEBOUNCE_MS,
} from '../../lib/chatTyping';
import {
  buildChatMessagesQuery,
  buildClosedChatSessionsQuery,
  buildOpenChatSessionsQuery,
  CHAT_SESSIONS_COLLECTION,
  isActiveChatStatus,
  normalizeChatMessageDoc,
  resolveTenantCompanyId,
} from '../../lib/chatQueries';
import { chatStatusLabel } from '../../lib/chatSessionStatus';
import { canAccessChatOperations } from '../../lib/userRoleHierarchy';
import { useAgentApps } from '../../hooks/useAgentApps';

const markChatSession = httpsCallable(functions, 'markChatSession');

function formatSessionTime(value) {
  if (!value) return '—';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function sessionLabel(session) {
  const name =
    typeof session?.visitorName === 'string' && session.visitorName.trim()
      ? session.visitorName.trim()
      : 'Visitor';
  const email =
    typeof session?.visitorEmail === 'string' && session.visitorEmail.trim()
      ? session.visitorEmail.trim()
      : '';
  return email ? `${name} (${email})` : name;
}

/**
 * Back Office live chat queue + archived history for agents and managers.
 */
export default function ChatOperationsHub({ userData }) {
  const companyId = useMemo(
    () => resolveTenantCompanyId(userData?.companyId),
    [userData?.companyId],
  );
  const role = userData?.role;
  const canAccess = canAccessChatOperations(role);
  const [searchParams, setSearchParams] = useSearchParams();

  const viewMode = searchParams.get('view') === 'history' ? 'history' : 'active';
  const selectedSessionId = (searchParams.get('sessionId') || '').trim();

  // Per-app (multi-brand) access boundary: only chat-enabled apps the user is
  // authorized for. Company admins see all; others see assigned apps only.
  const {
    apps: chatApps,
    loading: chatAppsLoading,
    isCompanyAdmin: isChatCompanyAdmin,
  } = useAgentApps('chat', userData);
  const authorizedAppIds = useMemo(() => chatApps.map((a) => a.id), [chatApps]);
  const [activeAppId, setActiveAppId] = useState('all');
  useEffect(() => {
    if (activeAppId !== 'all' && !authorizedAppIds.includes(activeAppId)) {
      setActiveAppId('all');
    }
  }, [authorizedAppIds, activeAppId]);
  const noChatApps = !chatAppsLoading && chatApps.length === 0;

  const [activeSessions, setActiveSessions] = useState([]);
  const [historySessions, setHistorySessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState(null);

  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [actionError, setActionError] = useState('');
  const [quickReplies, setQuickReplies] = useState([]);
  const [visitorTyping, setVisitorTyping] = useState(false);
  const [markingSession, setMarkingSession] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const idleTimerRef = useRef(null);

  const listSessions = useMemo(() => {
    const base = viewMode === 'history' ? historySessions : activeSessions;
    const appIdSet = new Set(authorizedAppIds);
    if (activeAppId === 'all') {
      // Company admins also see global (company-wide embed) sessions with no appId.
      return base.filter((s) =>
        s.appId ? appIdSet.has(s.appId) : isChatCompanyAdmin,
      );
    }
    return base.filter((s) => s.appId === activeAppId);
  }, [
    viewMode,
    historySessions,
    activeSessions,
    authorizedAppIds,
    activeAppId,
    isChatCompanyAdmin,
  ]);
  const isReadOnlyView = viewMode === 'history';
  const canReply =
    viewMode === 'active' && selectedSession && isActiveChatStatus(selectedSession.status);

  useEffect(() => {
    if (!companyId || !canAccess) {
      setQuickReplies([]);
      return undefined;
    }

    let cancelled = false;
    getChatSettings(companyId)
      .then((settings) => {
        if (cancelled) return;
        const rows = Array.isArray(settings?.quickReplies) ? settings.quickReplies : [];
        setQuickReplies(rows.filter((s) => typeof s === 'string' && s.trim()));
      })
      .catch((err) => {
        console.warn('ChatOperationsHub: quick replies load failed', err);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, canAccess]);

  useEffect(() => {
    const typing = normalizeTypingState(selectedSession?.typing);
    setVisitorTyping(typing.publicUser);
  }, [selectedSession?.typing, selectedSessionId]);

  useEffect(() => {
    if (!companyId || !canAccess) {
      setActiveSessions([]);
      setHistorySessions([]);
      setSessionsLoading(false);
      return undefined;
    }

    setSessionsLoading(true);
    let activeQuery;
    let historyQuery;
    try {
      activeQuery = buildOpenChatSessionsQuery(db, companyId);
      historyQuery = buildClosedChatSessionsQuery(db, companyId);
    } catch (error) {
      console.error('ChatOperationsHub: invalid sessions query', { companyId, error });
      setSessionsError('Could not load chat sessions (missing company).');
      setSessionsLoading(false);
      return undefined;
    }

    const unsubActive = onSnapshot(
      activeQuery,
      (snapshot) => {
        setActiveSessions(
          snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
        );
        setSessionsError(null);
        setSessionsLoading(false);
      },
      (error) => {
        console.error('ChatOperationsHub active sessions:', error);
        setSessionsError(error.message || 'Could not load active chat sessions.');
        setSessionsLoading(false);
      },
    );

    const unsubHistory = onSnapshot(
      historyQuery,
      (snapshot) => {
        setHistorySessions(
          snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
        );
      },
      (error) => {
        console.error('ChatOperationsHub history sessions:', error);
        setSessionsError((prev) => prev || error.message || 'Could not load chat history.');
      },
    );

    return () => {
      unsubActive();
      unsubHistory();
    };
  }, [companyId, canAccess]);

  useEffect(() => {
    if (!selectedSessionId || !companyId) {
      setSelectedSession(null);
      setSessionLoading(false);
      return undefined;
    }

    setSessionLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, CHAT_SESSIONS_COLLECTION, selectedSessionId),
      (snap) => {
        if (!snap.exists()) {
          setSelectedSession(null);
          setActionError('Session not found.');
          setSessionLoading(false);
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        if (data.companyId !== companyId) {
          setSelectedSession(null);
          setActionError('This session belongs to another company.');
          setSessionLoading(false);
          return;
        }
        setSelectedSession(data);
        setActionError('');
        setSessionLoading(false);
      },
      (error) => {
        console.error('ChatOperationsHub session doc:', error);
        setSelectedSession(null);
        setActionError(error.message || 'Could not load session.');
        setSessionLoading(false);
      },
    );

    return () => unsubscribe();
  }, [selectedSessionId, companyId]);

  useEffect(() => {
    if (!selectedSessionId || !companyId) {
      setMessages([]);
      return undefined;
    }

    setMessagesLoading(true);
    let messagesQuery;
    try {
      messagesQuery = buildChatMessagesQuery(db, companyId, selectedSessionId);
    } catch (error) {
      console.error('ChatOperationsHub: invalid messages query', {
        companyId,
        selectedSessionId,
        error,
      });
      setActionError('Could not load messages (missing company or session).');
      setMessagesLoading(false);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        setMessages(snapshot.docs.map((docSnap) => normalizeChatMessageDoc(docSnap)));
        setMessagesLoading(false);
      },
      (error) => {
        console.error('ChatOperationsHub messages:', error);
        setActionError(error.message || 'Could not load messages.');
        setMessagesLoading(false);
      },
    );

    return () => unsubscribe();
  }, [selectedSessionId, companyId]);

  const setViewMode = (nextView) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', nextView);
    next.delete('sessionId');
    setSearchParams(next, { replace: true });
    setReplyText('');
    setActionError('');
  };

  const selectSession = (sessionId) => {
    const next = new URLSearchParams(searchParams);
    if (sessionId) next.set('sessionId', sessionId);
    else next.delete('sessionId');
    setSearchParams(next, { replace: true });
    setActionError('');
  };

  const handleMarkSession = async (markAs) => {
    if (!selectedSessionId || markingSession || !canReply || !markAs) return;

    setMarkingSession(true);
    setActionError('');
    try {
      const result = await markChatSession({ sessionId: selectedSessionId, markAs });
      const payload = result?.data && typeof result.data === 'object' ? result.data : result;

      if (markAs === 'resolved_create_ticket') {
        const ticketId =
          typeof payload?.ticketId === 'string' ? payload.ticketId : '';
        setSnackbarMessage(
          ticketId
            ? `Chat resolved and Ticket #${ticketId} created successfully.`
            : 'Chat resolved and follow-up ticket created successfully.',
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        signalTypingDebounced.cancel();
        clearIdleTimer();
        setReplyText('');
        setViewMode('history');
      } else if (markAs === 'resolved') {
        setSnackbarMessage('Chat marked as resolved.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setReplyText('');
        setViewMode('history');
      } else {
        setSnackbarMessage(`Chat marked as ${chatStatusLabel(markAs)}.`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('markChatSession:', error);
      const message = error?.message || 'Could not update chat session status.';
      setActionError(message);
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setMarkingSession(false);
    }
  };

  const setAgentTyping = useCallback(
    async (isTyping) => {
      if (!selectedSessionId || !canReply) return;
      try {
        await updateDoc(doc(db, CHAT_SESSIONS_COLLECTION, selectedSessionId), {
          'typing.agent': isTyping,
        });
      } catch (err) {
        console.warn('ChatOperationsHub: agent typing update failed', err);
      }
    },
    [selectedSessionId, canReply],
  );

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const scheduleTypingIdle = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      setAgentTyping(false);
      idleTimerRef.current = null;
    }, TYPING_IDLE_MS);
  }, [clearIdleTimer, setAgentTyping]);

  const signalTypingDebounced = useMemo(
    () =>
      debounce(() => {
        setAgentTyping(true);
        scheduleTypingIdle();
      }, TYPING_SIGNAL_DEBOUNCE_MS),
    [setAgentTyping, scheduleTypingIdle],
  );

  useEffect(
    () => () => {
      signalTypingDebounced.cancel();
      clearIdleTimer();
    },
    [signalTypingDebounced, clearIdleTimer],
  );

  useEffect(() => {
    return () => {
      if (selectedSessionId) {
        updateDoc(doc(db, CHAT_SESSIONS_COLLECTION, selectedSessionId), {
          'typing.agent': false,
        }).catch(() => {});
      }
    };
  }, [selectedSessionId]);

  const handleReplyChange = (e) => {
    const next = e.target.value;
    setReplyText(next);

    if (!canReply || sending) return;

    if (!next.trim()) {
      signalTypingDebounced.cancel();
      clearIdleTimer();
      setAgentTyping(false);
      return;
    }

    signalTypingDebounced();
    scheduleTypingIdle();
  };

  const sendReplyText = async (text, options = {}) => {
    const trimmed = text.trim();
    const isInternal = options.isInternal === true;
    if (!trimmed || !selectedSessionId || !companyId || sending || !canReply) return;

    const senderId = auth.currentUser?.uid || null;
    if (!senderId) {
      setActionError('You must be signed in to send a reply.');
      return;
    }

    if (!isInternal) {
      signalTypingDebounced.cancel();
      clearIdleTimer();
      setAgentTyping(false);
    }

    setSending(true);
    setActionError('');
    try {
      await addDoc(collection(db, CHAT_SESSIONS_COLLECTION, selectedSessionId, 'messages'), {
        companyId,
        text: trimmed,
        message: trimmed,
        sender: 'agent',
        senderType: 'agent',
        senderId,
        agentUid: senderId,
        agentEmail: auth.currentUser?.email || '',
        createdAt: serverTimestamp(),
        createdByUid: senderId,
        timestamp: serverTimestamp(),
        ...(isInternal ? { isInternal: true } : {}),
      });

      if (isInternal) {
        await addDoc(collection(db, 'tickets'), {
          companyId,
          title: 'Internal Chat Note Reminder',
          description: trimmed,
          body: trimmed,
          status: 'open',
          priority: 'medium',
          source: 'chat_note',
          associatedChatId: selectedSessionId,
          chatSessionId: selectedSessionId,
          appId: selectedSession?.appId || null,
          labelIds: [],
          assigneeUid: null,
          assigneeAgentId: null,
          isAssignedToAi: false,
          assignedUsers: [],
          platformEscalation: false,
          archived: false,
          archivedAt: null,
          reporterUid: senderId,
          aiEnriched: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: senderId,
          createdByEmail: auth.currentUser?.email || null,
          updatedBy: senderId,
        });
        await updateDoc(doc(db, CHAT_SESSIONS_COLLECTION, selectedSessionId), {
          updatedAt: serverTimestamp(),
          'typing.agent': false,
        });
        setSnackbarMessage('Internal note saved and linked ticket created.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        await updateDoc(doc(db, CHAT_SESSIONS_COLLECTION, selectedSessionId), {
          status: 'awaiting_customer',
          updatedAt: serverTimestamp(),
          lastAgentReplyAt: serverTimestamp(),
          'typing.agent': false,
        });
      }
      setReplyText('');
    } catch (error) {
      console.error(isInternal ? 'save internal note:' : 'send agent reply:', error);
      setActionError(
        error.message || (isInternal ? 'Could not save internal note.' : 'Could not send reply.'),
      );
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async (e, options = {}) => {
    e?.preventDefault?.();
    await sendReplyText(replyText, options);
  };

  const handleQuickReply = async (template) => {
    await sendReplyText(template);
  };

  if (!companyId) {
    return (
      <Alert severity="info">Link your account to a company to manage live chat sessions.</Alert>
    );
  }

  if (!canAccess) {
    return (
      <Alert severity="warning">
        Live chat operations are available to Agents, Managers, and Company Admins when the Chat
        module is enabled.
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {noChatApps && !isChatCompanyAdmin ? (
        <Alert severity="info" sx={{ m: 1.5 }}>
          You&apos;re not assigned to any chat-enabled apps yet. Ask a company admin to assign you
          to an app in <strong>Product Portfolio</strong> (with the Chat module enabled).
        </Alert>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 0.75,
              minHeight: 44,
              flexShrink: 0,
              borderBottom: 1,
              borderColor: 'divider',
              flexWrap: 'wrap',
            }}
          >
            {chatApps.length > 0 && (
              <TextField
                select
                size="small"
                label="Active app"
                value={activeAppId}
                onChange={(e) => setActiveAppId(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="all">All my apps</MenuItem>
                {chatApps.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.appName}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <Tabs
              value={viewMode}
              onChange={(_e, value) => setViewMode(value)}
              sx={{
                minHeight: 36,
                '& .MuiTab-root': {
                  minHeight: 36,
                  py: 0.5,
                  textTransform: 'none',
                  fontWeight: 600,
                },
              }}
            >
              <Tab label="Active Sessions" value="active" />
              <Tab label="History" value="history" />
            </Tabs>

            {sessionsError ? (
              <Typography variant="caption" color="error" sx={{ ml: 'auto' }}>
                {sessionsError}
              </Typography>
            ) : null}
          </Box>

          <ResizableSplitLayout
            left={(
              <ChatQueueColumn
                viewMode={viewMode}
                sessions={listSessions}
                loading={sessionsLoading}
                selectedSessionId={selectedSessionId}
                onSelectSession={selectSession}
                formatSessionTime={formatSessionTime}
                sessionLabel={sessionLabel}
              />
            )}
            center={(
              <ChatStageColumn
                selectedSessionId={selectedSessionId}
                selectedSession={selectedSession}
                sessionLoading={sessionLoading}
                isReadOnlyView={isReadOnlyView}
                canReply={canReply}
                messages={messages}
                messagesLoading={messagesLoading}
                replyText={replyText}
                onReplyChange={handleReplyChange}
                onSendReply={handleSendReply}
                sending={sending}
                markingSession={markingSession}
                actionError={actionError}
                visitorTyping={visitorTyping}
                sessionLabel={sessionLabel}
                quickReplies={quickReplies}
                onQuickReply={handleQuickReply}
                onMarkSession={handleMarkSession}
              />
            )}
            right={(
              <ChatInfoPanel
                session={selectedSession}
                messages={messages}
                messagesLoading={messagesLoading}
                sessionLabel={sessionLabel}
                agentDisplayName={
                  userData?.displayName ||
                  userData?.email ||
                  auth.currentUser?.displayName ||
                  auth.currentUser?.email ||
                  'Unassigned'
                }
              />
            )}
          />
        </>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={7000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
